import { useMemo } from 'react';
import { useGetPropertyQuery } from '@/graphql/generated';
import textsData from '@/textData/CreateContractsTexts.json';
import { useUserLanguage, useCreateOwnerContractContext } from '@/customHooks';

export const SelectOwnerContractInsuranceMenu = () => {
  const { state, setInsuranceId } = useCreateOwnerContractContext();
  const userLanguage = useUserLanguage();

  const propertyId = state.propertyId ?? '';

  const { data: propertyData } = useGetPropertyQuery({
    variables: { id: propertyId },
    skip: !propertyId,
    fetchPolicy: 'network-only',
  });

  const insurances = useMemo(
    () => propertyData?.property?.insurances ?? [],
    [propertyData?.property?.insurances],
  );

  const rootTexts = userLanguage === 'es' ? textsData.es : textsData.en;
  const texts = rootTexts.ownerContract.insurance;

  const handleSelect = (insuranceId: string | null) => {
    setInsuranceId(insuranceId ?? null);
  };

  return (
    <div className="flex justify-center w-full">
      <div className="w-full max-w-5xl">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col gap-6">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">{texts.title}</h2>
            <p className="text-sm text-gray-500">{texts.helper}</p>
          </div>

          {!propertyId && (
            <div className="p-4 bg-red-50 text-red-600 rounded-xl text-center text-sm font-medium">
              {texts.mustSelectProperty}
            </div>
          )}

          {propertyId && insurances.length === 0 && (
            <div className="p-8 bg-gray-50 text-gray-500 rounded-xl text-center text-sm italic">
              {texts.noInsuranceText}
            </div>
          )}

          {propertyId && insurances.length > 0 && (
            <div className="flex flex-col gap-4">
              <div className="overflow-hidden border border-gray-100 rounded-xl">
                <table className="min-w-full text-sm divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-4 px-4 text-left font-semibold text-gray-600 w-12" />
                      <th className="py-4 px-4 text-left font-semibold text-gray-600">
                        {texts.table.company}
                      </th>
                      <th className="py-4 px-4 text-left font-semibold text-gray-600">
                        {texts.table.policy}
                      </th>
                      <th className="py-4 px-4 text-left font-semibold text-gray-600">
                        {texts.table.holder}
                      </th>
                      <th className="py-4 px-4 text-left font-semibold text-gray-600">
                        {texts.table.broker}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {insurances.map((insurance) => (
                      <tr
                        key={insurance.id}
                        onClick={() => handleSelect(insurance.id)}
                        className={`cursor-pointer transition-colors duration-150 ${
                          state.insuranceId === insurance.id ? 'bg-(--color-primary-50)' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="py-4 px-4">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            state.insuranceId === insurance.id 
                            ? 'border-(--color-primary) bg-(--color-primary)' 
                            : 'border-gray-300'
                          }`}>
                            {state.insuranceId === insurance.id && (
                                <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 font-medium text-gray-900">{insurance.insuranceCompany}</td>
                        <td className="py-4 px-4 text-gray-600">{insurance.policyNumber}</td>
                        <td className="py-4 px-4 text-gray-600">{insurance.insuranceHolder}</td>
                        <td className="py-4 px-4 text-gray-600">{insurance.insuranceBroker}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <button
                    type="button"
                    className="text-sm font-medium text-(--color-primary) hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(null);
                    }}
                >
                    {texts.noInsuranceChoice}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
